import React, { FC, useEffect, useRef } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import type { LogoContainerProps, LogoContainerAsLinkProps } from './LogoContainer.types';
import { StyledLogoContainer } from './LogoContainer.styles';
import clsx from 'clsx';
import { getLogoName } from '../../utils/helper';

const fetchSvg = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch SVG: ${response.statusText}`);
    }
    return await response.text();
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const LogoContainer: FC<LogoContainerProps> = (props: LogoContainerProps) => {
  const {
    brand,
    aspectRatio,
    width,
    color,
    height,
    alignment = 'Centered',
    isLink,
    linkUrl,
    hoverColor,
    backgroundColor,
    ariaRole,
    logopath,
    aspectMode,
    brandFullName,
    ...rest
  } = props;
  const svgContainerRef = useRef<HTMLSpanElement>(null);
  const logoName = getLogoName(brand);
  const brandName = brandFullName?.trim() || logoName;
  const nextData =
    (typeof window !== 'undefined' &&
      window.document.getElementById('__NEXT_DATA__') &&
      document.getElementById('__NEXT_DATA__')?.innerText) ||
    '';
  let isAuthorMode = false;
  if (nextData) {
    isAuthorMode = JSON.parse(nextData).props.pageProps.isAuthorMode;
  }

  const domainURL = isAuthorMode ? 'https://www.dev-cdn.marriott.com' : process.env['NEXT_PUBLIC_ASSET_DOMAIN'];

  const logoUrl = logopath || `${domainURL}/mi-assets/mi-global/brand-logos/${logoName}.svg`;

  useEffect(() => {
    const fetchSvgContent = async () => {
      const content = await fetchSvg(logoUrl);
      updateSvgContent(content || '');
    };

    fetchSvgContent();
  }, [logoUrl]);

  const updateSvgContent = (content: string) => {
    if (content) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'image/svg+xml');
      const svgElement = doc.querySelector('svg');

      const [scaleX, scaleY] = aspectRatio.split(':');

      if (svgElement !== null) {
        const wsf = Number(svgElement.getAttribute(`wsf-${scaleX}x${scaleY}`)).toFixed(2);
        const hsf = Number(svgElement.getAttribute(`hsf-${scaleX}x${scaleY}`)).toFixed(2);

        const scaledWidth = width * Number(wsf);
        const scaledHeight = height * Number(hsf);

        svgElement.style.width = `${scaledWidth}px`;
        svgElement.style.height = `${scaledHeight}px`;
        // Use setAttribute instead of direct assignment as preserveAspectRatio is readonly
        svgElement.setAttribute('preserveAspectRatio', aspectMode || '');
        svgElement.setAttribute('role', 'img');
        svgElement.setAttribute('aria-label', brandName);
        const stringifiedSVG: string = new XMLSerializer().serializeToString(svgElement);
        const sanitizedNode = DOMPurify.sanitize(stringifiedSVG, {
          RETURN_DOM_FRAGMENT: true,
        }) as DocumentFragment;
        svgContainerRef.current?.replaceChildren(sanitizedNode);
      }
    } else {
      svgContainerRef.current?.replaceChildren();
      console.log('No SVG elements found.');
    }
  };

  return (
    <StyledLogoContainer
      data-testid="ui-library-LogoContainer"
      className={clsx(backgroundColor)}
      data-component-name="m-ui-library-LogoContainer"
      as={isLink ? 'a' : 'span'}
      href={linkUrl || undefined}
      data-logoid={brand}
      containerWidth={width}
      containerHeight={height}
      isLink={isLink || false}
      hoverColor={hoverColor || ''}
      color={color || ''}
      {...rest}
    >
      <span
        ref={svgContainerRef}
        role={ariaRole}
        className={clsx(
          { 'bottom-left': alignment === 'Bottom Left' },
          { 'center-left': alignment === 'Center Left' },
          { 'bottom-center': alignment === 'Bottom Centered' },
          { centered: alignment === 'Centered' },
          { 'color-logo': color === 'color' },
          { 'background-aware-logo': color === 'background-aware' },
          { 'alternate-logo': color === 'alternate-logo' }
        )}
      ></span>
      {isLink && (rest as LogoContainerAsLinkProps)?.target === '_blank' && (
        <span className="sr-only">(opens in new window)</span>
      )}
    </StyledLogoContainer>
  );
};
